import React from 'react';
import { Link } from 'react-router-dom';
import { Terminal, Zap, Layers, Rocket, Palette, Brush, Settings } from 'lucide-react';
import { motion } from 'framer-motion';
import { YachtLogo } from '../components/YachtLogo';

import Navbar from '../components/Navbar';

const Documentation: React.FC = () => {
  return (
    <div className="min-h-screen bg-black text-white selection:bg-blue-500/30">
      <Navbar />

      {/* Content */}
      <main className="pt-32 pb-24 px-6 max-w-4xl mx-auto">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-16"
        >
          <div className="space-y-4">
            <h1 className="text-4xl md:text-5xl font-mono uppercase tracking-tighter font-bold">Documentation</h1>
            <p className="text-zinc-400 font-sans text-lg leading-relaxed max-w-2xl">
              Get started building right away with Yacht Labs. Follow this guide to navigate the platform and deploy your first project.
            </p>
          </div>

          <div className="space-y-12">
            {/* Step 1 */}
            <section className="space-y-4">
              <div className="flex items-center gap-3 text-blue-500 mb-6">
                <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center border border-blue-500/20">
                  <span className="font-mono text-sm font-bold">1</span>
                </div>
                <h2 className="text-xl font-mono uppercase tracking-widest text-white">Projects & Setup</h2>
              </div>
              <div className="pl-11 space-y-4 text-zinc-400 font-sans leading-relaxed">
                <p>On signing up, you can create a project and assign it a custom name.</p>
              </div>
            </section>

            {/* Step 2 */}
            <section className="space-y-4">
              <div className="flex items-center gap-3 text-purple-500 mb-6">
                <div className="w-8 h-8 rounded-full bg-purple-500/10 flex items-center justify-center border border-purple-500/20">
                  <Zap className="w-4 h-4" />
                </div>
                <h2 className="text-xl font-mono uppercase tracking-widest text-white">Think Tab</h2>
              </div>
              <div className="pl-11 space-y-4 text-zinc-400 font-sans leading-relaxed">
                <p>In the <strong>Think</strong> tab, click <em>Generate My Launch Package</em> or enter a custom app idea to start building.</p>
                <p>Once the Accelerator responds, click the Copy icon on the top right to copy your output to your clipboard.</p>
              </div>
            </section>

            {/* Step 3 */}
            <section className="space-y-4">
              <div className="flex items-center gap-3 text-emerald-500 mb-6">
                <div className="w-8 h-8 rounded-full bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20">
                  <Layers className="w-4 h-4" />
                </div>
                <h2 className="text-xl font-mono uppercase tracking-widest text-white">Build Tab</h2>
              </div>
              <div className="pl-11 space-y-4 text-zinc-400 font-sans leading-relaxed">
                <p>Navigate over to the <strong>Build</strong> tab, where you can paste your prompt output into the input box, select a format for the output (SaaS Landing recommended) and click <em>Build Product</em>. Alternatively, you can choose a template from the Template Library to get started building.</p>
                <p>Wait 200-300s for your build to render. Once generated, you can browse the preview HTML and system architecture assets, then deploy your build by clicking <em>Publish to Ship</em>.</p>
              </div>
            </section>

            {/* Step 4 */}
            <section className="space-y-4">
              <div className="flex items-center gap-3 text-orange-500 mb-6">
                <div className="w-8 h-8 rounded-full bg-orange-500/10 flex items-center justify-center border border-orange-500/20">
                  <Rocket className="w-4 h-4" />
                </div>
                <h2 className="text-xl font-mono uppercase tracking-widest text-white">Ship Tab</h2>
              </div>
              <div className="pl-11 space-y-4 text-zinc-400 font-sans leading-relaxed">
                <p>Navigate to <strong>Ship</strong> and select <em>Open Live</em> in the top right to view the public edge link for your build. Here you can download or share your build, and leave comments underneath the preview.</p>
                <p>Back in Yacht Labs, underneath Ship click <em>Generate Launch Package</em> to generate a set of custom engagement posts for different launch channels.</p>
              </div>
            </section>

            {/* Step 5 */}
            <section className="space-y-4">
              <div className="flex items-center gap-3 text-pink-500 mb-6">
                <div className="w-8 h-8 rounded-full bg-pink-500/10 flex items-center justify-center border border-pink-500/20">
                  <Palette className="w-4 h-4" />
                </div>
                <h2 className="text-xl font-mono uppercase tracking-widest text-white">Edit Tab</h2>
              </div>
              <div className="pl-11 space-y-4 text-zinc-400 font-sans leading-relaxed">
                <p>In the <strong>Edit</strong> tab (The editor), you can visually customize your build with a professional drag-and-drop editor. Rearrange sections, edit text, and preview responsive layouts across different devices.</p>
              </div>
            </section>

            {/* Step 6 */}
            <section className="space-y-4">
              <div className="flex items-center gap-3 text-cyan-500 mb-6">
                <div className="w-8 h-8 rounded-full bg-cyan-500/10 flex items-center justify-center border border-cyan-500/20">
                  <Brush className="w-4 h-4" />
                </div>
                <h2 className="text-xl font-mono uppercase tracking-widest text-white">Design Tab</h2>
              </div>
              <div className="pl-11 space-y-4 text-zinc-400 font-sans leading-relaxed">
                <p>Finally, in the <strong>Design</strong> Lab, architect your system's visual identity. Define primary palettes, typography scales, and component blueprints that maintain consistency across your entire ecosystem.</p>
              </div>
            </section>

            {/* Step 7 */}
            <section className="space-y-4">
              <div className="flex items-center gap-3 text-zinc-500 mb-6">
                <div className="w-8 h-8 rounded-full bg-zinc-500/10 flex items-center justify-center border border-zinc-500/20">
                  <Settings className="w-4 h-4" />
                </div>
                <h2 className="text-xl font-mono uppercase tracking-widest text-white">Settings</h2>
              </div>
              <div className="pl-11 space-y-4 text-zinc-400 font-sans leading-relaxed">
                <p>In <strong>Settings</strong>, manage your account and subscription, and view your plan details.</p>
              </div>
            </section>
          </div>
        </motion.div>
      </main>
      
      {/* Footer */}
      <footer className="border-t border-white/10 py-12 text-center">
        <p className="text-zinc-600 font-mono text-xs uppercase tracking-widest">© {new Date().getFullYear()} Yacht Labs. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default Documentation;
